import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "types";
import { fetchData } from "../api/apiHelpers";

export interface ContractsStatus {
  agreementStatus: "not_started" | "draft" | "pending_review" | "signed";
  agreementId: string | null;
  agreementSignedAt: string | null;
  consentAcknowledged: boolean;
  consentAcknowledgedAt: string | null;
  overallStatus: "not_started" | "in_progress" | "complete";
  userRole: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface ComplianceStatusResponse {
  agreement_status: string;
  agreement_id: string | null;
  agreement_signed_at: string | null;
  consent_acknowledged: boolean;
  consent_acknowledged_at: string | null;
  overall_status: string;
  user_role: string | null;
}

export function useContractsStatus(): ContractsStatus {
  const token = useSelector((s: RootState) => s.auth.token);
  const [data, setData] = useState<ComplianceStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const resp = await fetchData<ComplianceStatusResponse>(
        "/billing/legal/compliance-status/"
      );
      setData(resp);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch compliance status");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    agreementStatus: (data?.agreement_status as ContractsStatus["agreementStatus"]) ?? "not_started",
    agreementId: data?.agreement_id ?? null,
    agreementSignedAt: data?.agreement_signed_at ?? null,
    consentAcknowledged: data?.consent_acknowledged ?? false,
    consentAcknowledgedAt: data?.consent_acknowledged_at ?? null,
    overallStatus: (data?.overall_status as ContractsStatus["overallStatus"]) ?? "not_started",
    userRole: data?.user_role ?? null,
    loading,
    error,
    refresh: load,
  };
}
